import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Prop, State, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/init-services';
import { getStepsInRange } from '../../shared/get-steps-in-range';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';

@Component({
  tag: 'wb-value-slider',
  styleUrl: 'value-slider.scss',
  shadow: true,
})
export class ValueSliderComponent implements ComponentInterface {
  @Element()
  public el!: HTMLElement;

  /**
   * Colour scheme
   * @deprecated Use `data-on` attribute instead
   * */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** disables the input */
  @Prop({ reflect: true })
  public disabled: boolean;

  /** the range's label */
  @Prop()
  public label: string = 'label';

  /** the input's min attribute */
  @Prop()
  public min: number = 0;

  /** the input's max attribute */
  @Prop()
  public max: number = 100;

  /** the input's value attribute */
  @Prop({ mutable: true })
  public value: number = 50;

  /** the input's step attribute */
  @Prop()
  public step: number = 1;

  /** value to start a highlighted part on the track */
  @Prop()
  public highlightStart?: number;

  /** value to end a highlighted part on the track */
  @Prop()
  public highlightEnd?: number;

  /** number of scale steps; minimum value is `2`, marking the first and the last step */
  @Prop()
  public scaleSteps?: number;

  /** hides the tooltip shown on hover */
  @Prop({ reflect: true })
  public hideTooltip: boolean = false;

  /** function to format the selected value shown in the tooltip, defaults to toLocaleString */
  @Prop()
  public formatTooltipValue?: (value: number) => string = (value) => value.toLocaleString();

  /** function to format each step in the scale, defaults to toLocaleString */
  @Prop()
  public formatScaleStepValue?: (value: number) => string = (value) => value.toLocaleString();

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** emitted when the control's value changes */
  @Event()
  public wbinput: EventEmitter<number>;

  /** emitted when the control's value change */
  @Event()
  public wbchange: EventEmitter<number>;

  /** emitted when the control's value change */
  @Event({ composed: false })
  public change: EventEmitter<void>;

  /** emitted when the control gains focus */
  @Event()
  public wbfocus: EventEmitter<void>;

  /** emitted when the control loses focus */
  @Event()
  public wbblur: EventEmitter<void>;

  @Watch('value')
  watchPropHandler(newValue: number) {
    this.inputEl.value = String(newValue);
    this.updateOverlayStyles();
  }

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;

    const hasAlreadyRendered = Boolean(this.inputEl);

    if (hasAlreadyRendered) {
      this.updateOverlayStyles();
    }
  }

  @State()
  private isActive = false;

  private TagNames = transformTagNames(this, 'wb-value-slider', ['wb-text']);

  private inputEl;
  private indicatorEl;
  private highlightLineEl;
  private progressLineEl;

  public connectedCallback(): void {
    this.updateDirection();
  }

  public componentDidLoad(): void {
    this.updateOverlayStyles();
  }

  public onInput = (): void => {
    this.updateOverlayStyles();
    this.emitInputEvent();
  };

  public onChange = () => {
    this.emitChangeEvent();
  };

  public onFocus = (): void => {
    this.wbfocus.emit();
  };

  public onBlur = (): void => {
    this.wbblur.emit();
  };

  public onTouchStart = (): void => {
    this.isActive = true;
  };

  public onTouchEnd = (): void => {
    this.isActive = false;
  };

  public emitInputEvent(): void {
    this.wbinput.emit(Number(this.inputEl.value));
  }

  public emitChangeEvent = (): void => {
    const value = Number(this.inputEl.value);
    this.value = value;
    this.wbchange.emit(value);
    this.change.emit();
  };

  private updateOverlayStyles(): void {
    const valueRange = this.max - this.min;

    this.updateProgressLineAndIndicator(valueRange);

    if (this.highlightLineEl) {
      this.updateHighlightLine(valueRange);
    }
  }

  private updateProgressLineAndIndicator(valueRange: number): void {
    const indicatorOffsetStartPercentage = ((this.inputEl.value - this.min) / valueRange) * 100;

    if (this.isRtl) {
      this.indicatorEl.style.left = 'initial';
      this.indicatorEl.style.right = `${indicatorOffsetStartPercentage}%`;
    } else {
      this.indicatorEl.style.left = `${indicatorOffsetStartPercentage}%`;
      this.indicatorEl.style.right = 'initial';
    }

    this.indicatorEl.setAttribute('data-value', this.formatTooltipValue(this.inputEl.value)); // labels for tooltips
    this.progressLineEl.style.width = `${indicatorOffsetStartPercentage}%`;
  }

  private updateHighlightLine(valueRange: number): void {
    const highlightStartOffsetStartPercentage = ((this.highlightStart - this.min) / valueRange) * 100;
    const lineWidthPercentage = ((this.highlightEnd - this.highlightStart) / valueRange) * 100;

    if (this.isRtl) {
      this.highlightLineEl.style.left = 'initial';
      this.highlightLineEl.style.right = `${highlightStartOffsetStartPercentage}%`;
    } else {
      this.highlightLineEl.style.left = `${highlightStartOffsetStartPercentage}%`;
      this.highlightLineEl.style.right = 'initial';
    }

    this.highlightLineEl.style.width = `${lineWidthPercentage}%`;
  }

  private renderScaleDots(): JSX.Element {
    const els: Array<any> = [];

    for (let i = 0; i < this.scaleSteps; i++) {
      els.push(<div class="dot" />);
    }

    return <div class="dots">{els}</div>;
  }

  private renderScaleNumbers(): JSX.Element {
    const { WbText } = this.TagNames;
    const steps = getStepsInRange(this.min, this.max, this.scaleSteps);

    return (
      <div class="scale">
        {steps.map((value) => (
          <div class="scale-item">
            <WbText size="m" static>
              {this.formatScaleStepValue(value)}
            </WbText>
          </div>
        ))}
      </div>
    );
  }

  private refInput = (el) => (this.inputEl = el);
  private refIndicator = (el) => (this.indicatorEl = el);
  private refHighlight = (el) => (this.highlightLineEl = el);
  private refProgressLine = (el) => (this.progressLineEl = el);

  public render(): JSX.Element {
    const { WbText } = this.TagNames;
    const hasValidScaleSettings = this.scaleSteps > 2;

    return (
      <Host class="wb-value-slider" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <WbText class="label" size="l" static>
          <label htmlFor="range">{this.label}</label>
        </WbText>
        <div class="inner">
          <input //
            ref={this.refInput}
            class={{ active: this.isActive }}
            onInput={this.onInput}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onTouchStart={this.onTouchStart}
            onTouchEnd={this.onTouchEnd}
            id="range"
            name="range"
            type="range"
            disabled={this.disabled}
            min={this.min}
            max={this.max}
            step={this.step}
            value={this.value}
          />
          <div class="overlay">
            <div class="indicator" ref={this.refIndicator} data-testid="indicator" />
            <div class="progress-line" ref={this.refProgressLine} />
            {this.highlightStart && this.highlightEnd && <div class="highlight-area" ref={this.refHighlight} />}
            {hasValidScaleSettings && this.renderScaleDots()}
          </div>
        </div>
        {hasValidScaleSettings && this.renderScaleNumbers()}
      </Host>
    );
  }
}
